import { useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { Modal, Button, Alert } from 'react-bootstrap';
import {
  useCreateSponsorshipsBatch,
  useBeneficiary
} from 'hooks/useBeneficiaries';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import paths from 'routes/paths';
import SponsorshipForm from './sponsorship-form/SponsorshipForm';
import { formatMoney } from 'helpers/utils';

export default function CreateSponsorship() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  // The form is reached either via /sponsorship/create?beneficiaryId=... (query
  // param) or via /beneficiaries/:id/donors/:donorId/sponsorships/create (route
  // param). Read both, otherwise beneficiaryId is null on the route-param path
  // and the sponsorship insert sends orphan_id = null, which the RLS policy
  // rejects ("new row violates row-level security policy").
  const beneficiaryId =
    searchParams.get('beneficiaryId') || searchParams.get('id') || params.id;

  const [validationErrors, setValidationErrors] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const { createSponsorshipsBatchAsync, isCreating: loading } =
    useCreateSponsorshipsBatch();
  const { beneficiary, isLoading: beneficiaryLoading } =
    useBeneficiary(beneficiaryId);

  const onSubmit = async items => {
    setValidationErrors([]);

    // Guard: without a beneficiary the insert would send orphan_id = null and
    // fail with a confusing RLS error. Surface a clear message instead.
    if (!beneficiaryId) {
      toast.error('Please select a beneficiary to sponsor before saving.');
      return;
    }

    try {
      const result = await createSponsorshipsBatchAsync({
        items,
        beneficiaryId,
        branchId: beneficiary?.branch_id
      });
      console.log('Create result:', result);

      toast.success(
        `Successfully created ${items.length} sponsorship${
          items.length > 1 ? 's' : ''
        }`
      );
      navigate(paths.sponsorshipManagement);
    } catch (err) {
      console.error('Error creating sponsorship:', err);

      if (err.validationErrors) {
        // Validation failed - show specific errors
        setValidationErrors(err.validationErrors);
        setShowErrorModal(true);
      } else if (err.partialSuccess) {
        // Some items were created before failure
        toast.warning(
          'Some sponsorships were created but others failed. Please check and try again.'
        );
        navigate(paths.sponsorshipManagement);
      } else {
        toast.error('Failed to create sponsorship: ' + err.message);
      }
    }
  };

  return (
    <>
      <SponsorshipForm
        onSubmit={onSubmit}
        loading={loading}
        beneficiary={beneficiary}
        beneficiaryLoading={beneficiaryLoading}
      />

      {/* Validation Error Modal */}
      <Modal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        centered
      >
        <Modal.Header closeButton className="bg-soft-danger">
          <Modal.Title>
            <FontAwesomeIcon
              icon="exclamation-triangle"
              className="text-danger me-2"
            />
            Validation Failed
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-700 mb-3">
            Some donation balances have changed since you started. This can
            happen if another user allocated funds while you were working.
          </p>

          <Alert variant="danger">
            <h6 className="mb-2">Issues found:</h6>
            <ul className="mb-0">
              {validationErrors.map((error, idx) => (
                <li key={idx}>
                  {error.type === 'insufficient' ? (
                    <>
                      <strong>{error.donorName}</strong>: Needs{' '}
                      {formatMoney(error.required)} but only{' '}
                      {formatMoney(error.available)} available
                    </>
                  ) : (
                    error.message
                  )}
                </li>
              ))}
            </ul>
          </Alert>

          <p className="text-muted mb-0 fs--1">
            Please go back and adjust the sponsorship items, then try again.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
            Go Back and Fix
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
